import { useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorHandler from '@Root/ErrorHandler';
import { IoClose as Close } from 'react-icons/io5';
import {
    Trash2 as DeleteIcon,
    File as FileIcon,
    AlertTriangle,
} from 'lucide-react';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import BorderRedButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderRedButtonV2';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import FileService from '@Helpers/URLHelpers/FileService';
import './DeleteFileForm.scss';

export default function DeleteFileForm(props) {

    const [deleting, setDeleting] = useState(false);
    const { currentProjectId } = useReduxProject();
    const { currentLeadsId } = useReduxLeads();
    const fileService = new FileService();
    const errorHandler = new ErrorHandler();
    const className = 'deleteFileForm';

    const handleDelete = async (e) => {
        e?.preventDefault();
        if (deleting) return;
        setDeleting(true);
        try {
            const body = {
                fileName: props.fileName,
                tableType: 'LEADS_WHATSAPP',
                itemId: currentLeadsId,
                fileId: props.fileId,
            };
            await fileService.deleteFileForLeads(body);
            await props.renderData();
            props.closePopup();
        } catch (err) {
            errorHandler.handleError(err);
            setDeleting(false);
        }
    };

    const handleClose = () => {
        if (deleting) return;
        props.closePopup();
    };

    return (
        <Modal
            open={props.open}
            onClose={handleClose}
            className={className}
        >
            <Box className={`${className}__paper`}>

                {/* Header */}
                <Box className={`${className}__header`}>

                    <Box className={`${className}__header-content`}>
                        <Box className={`${className}__file-icon`}>
                            <FileIcon />
                        </Box>

                        <Box className={`${className}__heading`}>
                            <Typography
                                className={`${className}__eyebrow`}
                            >
                                File management
                            </Typography>

                            <Typography
                                className={`${className}__title`}
                            >
                                Delete file
                            </Typography>
                        </Box>
                    </Box>

                    <Close
                        className={`${className}__close`}
                        onClick={handleClose}
                    />
                </Box>

                <Box className={`${className}__content`}>

                    <Box className={`${className}__file-card`}>
                        <Box className={`${className}__file-card-icon`}>
                            <FileIcon />
                        </Box>

                        <Box className={`${className}__file-info`}>
                            <Typography
                                className={`${className}__file-label`}
                            >
                                File
                            </Typography>

                            <Typography
                                className={`${className}__file-name`}
                                title={props.fileName}
                            >
                                {props.fileName}
                            </Typography>
                        </Box>
                    </Box>

                    <Box className={`${className}__warning`}>
                        <Box className={`${className}__warning-icon`}>
                            <AlertTriangle />
                        </Box>

                        <Box className={`${className}__warning-content`}>
                            <Typography
                                className={`${className}__warning-title`}
                            >
                                This action cannot be undone
                            </Typography>

                            <Typography
                                className={`${className}__warning-text`}
                            >
                                All data and content in this file will be
                                permanently deleted and cannot be recovered.
                            </Typography>
                        </Box>
                    </Box>

                    <Typography
                        className={`${className}__confirmation`}
                    >
                        Are you sure you want to delete this file ?
                    </Typography>
                </Box>

                <Box className={`${className}__footer`}>
                    <button
                        type="button"
                        className={`${className}__footer-button ${className}__footer-button--cancel`}
                        onClick={handleClose}
                        disabled={deleting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`${className}__footer-button ${className}__footer-button--delete`}
                        onClick={handleDelete}
                        disabled={deleting}
                    >
                        {deleting ? (
                            <>
                                <CircularProgress
                                    size={14}
                                    color="inherit"
                                />
                                <span>Deleting...</span>
                            </>
                        ) : (
                            <>
                                <DeleteIcon size={15} />
                                <span>Delete File</span>
                            </>
                        )}
                    </button>
                </Box>
                {deleting && (
                    <Box className={`${className}__loading`}>
                        <CircularProgress
                            size={18}
                            color="inherit"
                        />

                        <Typography>
                            Deleting file...
                        </Typography>
                    </Box>
                )}
            </Box>
        </Modal>
    );
}
